<template>
  <k-modal v-if="isReady" id="add-new-otj-modal" :show="showModal"
    @close="$emit('close')"
    :closeOnClickAway="false"
  >
    <template #header>
      <h3>{{ modalTitle }}</h3>
    </template>
    <template #body>
      <div class="modal-body">
        <div v-if="complianceMode" class="disclaimer">
          <h4>Note<i class="fas fa-exclamation-triangle"></i></h4>
          <p>
            You are directly editing a learner's O.T.J. entry and your changes will be saved to their record.
            This functionality should only be used to fix errors or issues in their O.T.J. submissions that
            cannot be resolved by the usual review, approval and rejection workflow between the learner and coach.
          </p>
        </div>
        <div v-if="isFutureDate" class="disclaimer future-date-disclaimer">
          <h4>Note<i class="fas fa-exclamation-triangle"></i></h4>
          <p>
            You are trying to submit an O.T.J. for a date in the future.
            This is not allowed, please select the current date or save this as a draft for future submission.
          </p>
        </div>
        <form class="form-date-picker">
          <fieldset class="form-group form-entry form-required">
            <k-date-picker
              v-model="newOtjDateCompleted"
              label="Date"
              :popover="{ visibility: 'focus' }"
              :modelConfig="{ timeAdjust: '00:00:00' }"
            />
          </fieldset>
          <fieldset class="form-group form-entry">
            <k-date-picker
              v-model="newOtjStartTime"
              label="Start Time"
              type="time"
              mode="time"
              input-placeholder="Select time..."
              :popover="{ visibility: 'focus' }"
              :modelConfig="{ timeAdjust: '00:00:00' }"
            />
          </fieldset>
          <fieldset class="form-group form-entry">
            <k-date-picker
              v-model="newOtjEndTime"
              label="End Time"
              type="time"
              mode="time"
              input-placeholder="Select time..."
              :popover="{ visibility: 'focus' }"
              :modelConfig="{ timeAdjust: '00:00:00' }"
            />
          </fieldset>
          <fieldset class="form-group form-entry">
            <div class="otj-total-hours-container">
              <label for="otj-total-hours">Total Hours</label>
              <input class="form-control" id="otj-total-hours" type="text" v-model="formatTimeSpent" readonly />
            </div>
          </fieldset>
        </form>
        <div class="learning-activity-container">
          <div class="dropdown-container form-required">
            <label>Learning Activity Type</label>
            <k-dropdown
              v-model="newOtjLearningActivityType"
              :options="learningActivityTypes"
              :show-search="false"
              placeholder="Select Learning Activity Type"
            />
          </div>
          <div v-if="!complianceMode" class="expandable-container">
            <div class="expandable-header">
              <button class="btn btn-link" @click="toggleExpandableContent" aria-label="Toggle Learning Activity Type">
                <span>Example of OTJ Learning Activity Type<i class="fas" :class="`fa-angle-${showExpandableContent ? 'up' : 'down'}`"></i></span>
              </button>
            </div>
            <div class="expandable-content" v-if="showExpandableContent">
              <ul>
                <li v-for="learningActivityType in learningActivityTypes" :key="learningActivityType.id">
                  <strong>{{ learningActivityType.name }}</strong>
                  <p>{{ learningActivityType.description }}</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <fieldset class="form-group comment-container form-required">
          <label for="otj-learner-comment">
            What have you learned that you didn't know before undertaking this activity?
            <span class="character-count" :class="{ 'near-limit': newOtjLearningDescription.length > descMaxLength * 0.9 }">
              {{ newOtjLearningDescription.length }}/{{ descMaxLength }} characters
            </span>
          </label>
          <textarea
              id="otj-learner-comment"
              placeholder="Type your comment here..."
              v-model="newOtjLearningDescription"
              rows="3"
              :maxlength="descMaxLength"/>
        </fieldset>
        <div class="competency-container">
          <span class="label-text">Select Competencies relevant to the Learning Activity</span>
          <template v-if="competenciesReady && competencies.length > 0">
            <k-editable-table
              v-model="competencies"
              :headers="tableHeaders"
              :max="10"
            />
          </template>
          <div v-else>
            <span class="label-text">No competencies found for this programme</span>
          </div>
        </div>
        <div v-if="!complianceMode" class="confirmation-container form-required">
          <label>
            <input type="checkbox" v-model="otjConfirmation"/>I confirm that all off-the-job training took place during my paid working hours and was directly relevant to my apprenticeship,
            providing new knowledge, skills, or behaviours needed for achievement.
          </label>
        </div>
        <div v-if="complianceMode" class="status-container form-required">
          <label>Status</label>
          <div style="display: flex;">
            <template v-if="editStatus">
              <ul class="status-options">
                <li v-for="status in statusOptions">
                  <button @click="selectStatus(status)">
                    <span class="status-badge" :class="`${status}`">{{ status }}</span>
                  </button>
                </li>
              </ul>
            </template>
            <template v-else>
              <span class="status-badge" :class="`${newOtjStatus}`">
                {{ newOtjStatus }}
              </span>
              <k-icon-button icon="fas fa-pencil-alt" classes="btn btn-primary" aria-label="Edit status" @click="editStatus = true"/>
            </template>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="modal-footer-left">
        <button class="btn btn-outlined" @click="$emit('close')">Cancel</button>
      </div>
      <div class="modal-footer-right">
        <template v-if="complianceMode">
          <button v-if="complianceMode" class="btn btn-danger" @click="confirmOtjDeletion" :disabled="saving">
            Delete
          </button>
          <button class="btn btn-success" @click="saveChanges(newOtjStatus)" :disabled="!validSubmitForReview">
            <i v-if="saving" class="fas fa-spinner fa-spin"></i>
            Update
          </button>
        </template>
        <template v-else>
          <button class="btn btn-default" @click="saveChanges('draft')" :disabled="!validSaveAsDraft">
            <i v-if="saving" class="fas fa-spinner fa-spin"></i>
            Save as Draft
          </button>
          <button class="btn btn-success" @click="saveChanges('pending')" :disabled="!validSubmitForReview">
            <i v-if="saving" class="fas fa-spinner fa-spin"></i>
            Submit for Review
          </button>
        </template>
      </div>
    </template>
  </k-modal>
</template>

<style>
#add-new-otj-modal .modal-footer {
  display: flex;
  justify-content: space-between;
}

#add-new-otj-modal .modal-container {
  width: 80vw;
}

#add-new-otj-modal .form-control[readonly] {
  background-color: var(--input-background);
  border: var(--input-border);
}

.status-container .k-icon-button {
  margin-left: 10px;
}
</style>

<style scoped>
.modal-body {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.form-date-picker {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;
}

.learning-activity-container {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.comment-container,
.otj-total-hours-container {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
}

.comment-container > label {
  width: 100%;
}

.status-container {
  display: flex;
  flex-direction: column;
}

ul.status-options {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: row;
  gap: 5px;
}

ul.status-options li > button {
  background-color: transparent;
}

.character-count {
  float: right;
  font-size: 0.8em;
  color: var(--kate-type-secondary);
  margin-top: 4px;
  display: inline-block;
}

.character-count.near-limit {
  color: var(--kate-type-danger);
}

.comment-container textarea {
  width: 100%;
  min-height: 80px;
  padding: 10px;
  border: var(--input-border-alt);
  border-radius: 4px;
  resize: vertical;
}

.btn-link:focus {
  color: var(--kate-type-link);
  outline-offset: 2px;
}

.btn-link i {
  margin-left: 5px;
}

.expandable-content {
  background-color: var(--kate-background-alt);
  padding: 10px;
  margin-top: 10px;
  border-radius: 8px;
  box-shadow: 0 0 1px 2px var(--kate-background-alpha);
}

.confirmation-container input {
  margin-right: 5px;
}

.confirmation-container label {
  cursor: pointer;
}

.confirmation-container label:hover {
  text-decoration: underline;
}

.competency-container {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

i.fas.fa-spinner.fa-spin {
  width: fit-content;
}

#otj-total-hours[readonly]:focus {
  outline: none;
  box-shadow: none;
}

#otj-total-hours[readonly] {
  text-align: center;
}

.disclaimer {
  background-color: var(--kate-background-alt);
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0 0 1px 2px var(--kate-background-alpha);
}

.disclaimer h4 {
  color: var(--kate-type-warning);
  margin: 0;
}

.disclaimer h4 i {
  float: right;
  margin-left: 10px;
}
</style>

<script>
import KModal from '@base-components/k-modal.vue';
import KDatePicker from '@base-components/k-date-picker.vue';
import KDropdown from '@base-components/k-dropdown.vue';
import KEditableTable from '@base-components/k-editable-table.vue';
import KIconButton from '@base-components/buttons/k-icon-button.vue';
import TimeMixin from '@mixins/time-mixins';
import { LEARNING_ACTIVITY_TYPES } from '@constants';

const DESC_MAX_LENGTH = 2048;

export default {
  components: {
    KModal,
    KDatePicker,
    KDropdown,
    KEditableTable,
    KIconButton,
  },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    programmeId: {
      type: Number,
      required: true,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    otjData: {
      type: Object,
      default: undefined,
    },
    // Compliance mode props
    complianceMode: {
      type: Boolean,
      default: false,
    },
    learnerId: {
      type: Number,
      required: false,
    },
    programmeMemberId: {
      type: Number,
      required: false,
    },
  },

  mixins: [TimeMixin],

  data() {
    return {
      newOtjLearningDescription: '',
      newOtjDateCompleted: new Date(),
      newOtjStartTime: new Date(),
      newOtjEndTime: new Date(),
      newOtjTimeSpent: 0,
      newOtjLearningActivityType: undefined,
      newOtjStatus: this.otjData?.status,
      learningActivityTypes: LEARNING_ACTIVITY_TYPES,
      showExpandableContent: false,
      draftCompetencies: [],
      competencies: [],
      competenciesReady: false,
      otjConfirmation: false,
      isReady: true,
      saving: false,
      selectedCompetencies: [],
      descMaxLength: DESC_MAX_LENGTH,
      editStatus: false,
    };
  },

  watch: {
    showModal(newVal) {
      if (newVal) {
        this.getCompetencies();
      }
    },

    otjData: {
      handler(newVal) {
        if (newVal) {
          this.newOtjDateCompleted = newVal.date_completed ? this.getDate(newVal.date_completed) : new Date();
          this.newOtjStartTime = newVal.start_time ? this.getDate(newVal.start_time) : new Date();
          this.newOtjEndTime = newVal.end_time ? this.getDate(newVal.end_time) : new Date();
          this.newOtjTimeSpent = newVal.time_spent || 0;
          this.newOtjStatus = newVal.status;
          // This is broken when we edit a draft otj and try to change the start/end time and this doesn't update the time spent

          // Set learning activity type
          if (newVal.category) {
            const activityType = this.learningActivityTypes.find(
              type => type.name === newVal.category,
            );
            this.newOtjLearningActivityType = activityType ? activityType.id : undefined;
          } else {
            this.newOtjLearningActivityType = undefined;
          }

          this.newOtjLearningDescription = newVal.learning_description || '';
          this.otjConfirmation = newVal.declared_in_working_hours || false;
          this.draftCompetencies = newVal.competencies || [];

          // If competencies are already loaded, mark the selected ones
          // We might not need this anymore since we're doing it in getCompetencies
          if (this.competenciesReady && this.competencies.length > 0) {
            this.markSelectedCompetencies(this.draftCompetencies);
          }
        } else {
          // Reset form when no OTJ data is provided
          this.clearFields();
        }
      },
      immediate: true,
    },
  },

  computed: {
    modalTitle() {
      if (!this.isEditing) {
        return 'Add New O.T.J.';
      }
      if (this.otjData?.status.toLowerCase() === 'rejected') {
        return 'Edit Rejected O.T.J.';
      } if (this.otjData?.status.toLowerCase() === 'draft') {
        return 'Edit Draft O.T.J.';
      }
      return 'Edit O.T.J.';
    },

    isFutureDate() {
      return this.newOtjDateCompleted > new Date();
    },

    validSubmitForReview() {
      return Boolean(this.otjConfirmation && this.validDescription && this.newOtjLearningActivityType && this.newOtjDateCompleted && !this.saving && !this.isFutureDate);
    },

    validSaveAsDraft() {
      return Boolean(!this.saving);
    },

    validDescription() {
      return this.newOtjLearningDescription.trim() !== '';
    },

    formatTimeSpent() {
      // For new OTJs or when start/end times have changed, calculate from start and end times
      if (!this.newOtjStartTime || !this.newOtjEndTime) {
        return '';
      }

      const diffHours = this.newOtjEndTime.getHours() - this.newOtjStartTime.getHours();
      const diffMinutes = 60 * diffHours + this.newOtjEndTime.getMinutes() - this.newOtjStartTime.getMinutes();

      if (diffMinutes < 0) {
        return 'Invalid time range';
      }

      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      return `${hours}h ${minutes}m`;
    },

    tableHeaders() {
      return [
        {
          key: 'checkbox',
          title: '',
          editable: true,
          type: 'boolean',
          required: true,
          sortable: false,
        },
        {
          key: 'label',
          title: 'Label',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
        },
        {
          key: 'category',
          title: 'Category',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
        },
        {
          key: 'description',
          title: 'Description',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
        },
      ];
    },

    statusOptions() {
      return ['pending', 'approved', 'rejected'];
    },

    updateOtjEntryUrl() {
      return `/api/curriculum/${this.complianceMode ? 'admin/' : ''}programmes/${this.programmeId}/otj-entries/${this.otjData.id}`;
    },
  },

  methods: {
    getOtjEntryPayload(status) {
      const payload = {
        date_completed: this.formatDate(this.newOtjDateCompleted),
        time_spent: this.timeSpentMinutes(),
        category: this.getLearningActivityType()?.name || '',
        learning_description: this.newOtjLearningDescription || '',
        declared_in_working_hours: this.otjConfirmation,
        programme_competency_ids: this.listSelectedCompetencies().map(competency => competency.id),
        status,
        start_time: this.formatDate(this.overrideDateComponent(this.newOtjStartTime, this.newOtjDateCompleted)),
        end_time: this.formatDate(this.overrideDateComponent(this.newOtjEndTime, this.newOtjDateCompleted)),
      };
      if (this.complianceMode) {
        payload.user_id = this.learnerId;
        payload.programme_member_id = this.programmeMemberId;
      }
      return payload;
    },

    getCompetencies() {
      this.isReady = false;
      this.$Loading.minimal();
      this.$logger.info('Fetching competencies', true);
      this.$http.get(`/api/curriculum/programmes/${this.programmeId}/competencies/scores`)
        .then(response => {
          this.competencies = response.data.competencies;
          this.competenciesReady = true;

          // If we have OTJ data, mark the selected competencies
          if (this.otjData && this.otjData.competencies) {
            this.markSelectedCompetencies(this.otjData.competencies);
          }
        })
        .catch(error => {
          this.$logger.error('Error fetching competencies', error);
          this.competenciesReady = true;
        }).then(() => {
          this.isReady = true;
          this.$Loading.finish();
        });
    },

    toggleExpandableContent() {
      this.showExpandableContent = !this.showExpandableContent;
    },

    markSelectedCompetencies(draftCompetencies) {
      if (!this.otjData || !this.otjData.competencies || !this.competencies) {
        return;
      }

      // Get the IDs of competencies from the OTJ data
      const selectedIds = draftCompetencies.map(c => c.id);

      // Create a completely new array to ensure Vue reactivity
      const updatedCompetencies = [];

      // Add each competency with the checkbox property set
      this.competencies.forEach(competency => {
        updatedCompetencies.push({
          ...competency,
          checkbox: selectedIds.includes(competency.id),
        });
      });

      // Replace the entire array to trigger reactivity
      this.competencies = updatedCompetencies;
    },

    listSelectedCompetencies() {
      return this.competencies.filter(competency => competency.checkbox);
    },

    getLearningActivityType() {
      return this.learningActivityTypes.find(type => type.id === this.newOtjLearningActivityType);
    },

    selectStatus(status) {
      this.newOtjStatus = status;
      this.editStatus = false;
    },

    timeSpentMinutes() {
      // For new OTJs or when start/end times have changed, calculate from start and end times
      if (!this.newOtjStartTime || !this.newOtjEndTime) {
        return 0;
      }

      const diffHours = this.newOtjEndTime.getHours() - this.newOtjStartTime.getHours();
      const diffMinutes = 60 * diffHours + this.newOtjEndTime.getMinutes() - this.newOtjStartTime.getMinutes();

      if (diffMinutes < 0) {
        return 0;
      }

      return diffMinutes;
    },

    createOtjEntry(status) {
      this.saving = true;
      const payload = this.getOtjEntryPayload(status);
      this.$logger.info('Creating OTJ entry', { status }, true);
      const toastNoun = status === 'draft' ? 'draft O.T.J.' : 'O.T.J.';
      this.$http.post(`/api/curriculum/programmes/${this.programmeId}/otj-entries`, payload)
        .then(() => {
          this.$ktoast.success(`${status === 'draft' ? 'Saved' : 'Submitted'} ${toastNoun}`);
          this.$logger.info('OTJ entry created', { status }, true);
          this.$emit('close');
          this.$emit('update');
          this.clearFields();
        }).catch(err => {
          this.$logger.autowarn('Error creating OTJ entry', { status }, err);
          if (err.response.status === 400) {
            this.$ktoast.showError(err);
          } else {
            this.$ktoast.warning(`Error ${status === 'draft' ? 'saving' : 'submitting'} ${toastNoun}`);
          }
        }).then(() => {
          this.saving = false;
        });
    },

    updateOtjEntry(status) {
      this.saving = true;
      const payload = this.getOtjEntryPayload(status);
      const logPayload = { otjEntryId: this.otjData.id, status, complianceMode: this.complianceMode };
      const toastNoun = status === 'draft' ? 'draft O.T.J.' : 'O.T.J.';
      this.$logger.info('Updating OTJ entry', logPayload, true);
      this.$http.put(this.updateOtjEntryUrl, payload)
        .then(() => {
          let toastVerb = status === 'draft' ? 'Saved' : 'Submitted';
          if (this.complianceMode) {
            toastVerb = 'Updated';
          }
          this.$ktoast.success(`${toastVerb} ${toastNoun}`);
          this.$logger.info('OTJ entry updated', logPayload, true);
          this.$emit('close');
          this.$emit('update');
          this.clearFields();
        }).catch(err => {
          this.$logger.autowarn('Error updating OTJ entry', logPayload, err);
          if (err.response.status === 400 && err.response.data.err === 'user_not_in_programme') {
            this.$ktoast.showError(err);
          } else {
            let toastVerb = status === 'draft' ? 'saving' : 'submitting';
            if (this.complianceMode) {
              toastVerb = 'updating';
            }
            this.$ktoast.warning(`Error ${toastVerb} ${toastNoun}`);
          }
        }).then(() => {
          this.saving = false;
        });
    },

    submitRejectedOtj(status) {
      if (!this.otjData || !this.programmeId) {
        return;
      }
      this.saving = true;
      const payload = this.getOtjEntryPayload(status);
      this.$http.post(`/api/curriculum/programmes/${this.programmeId}/otj-entries/${this.otjData.id}/re-submission`, payload)
        .then(() => {
          this.$logger.info('OTJ re-submitted', { otjId: this.otjData.id });
          this.$ktoast.success('OTJ re-submitted');
          this.$emit('close');
          this.$emit('update');
        })
        .catch(err => {
          this.$logger.error('Error submitting rejected OTJ', { otjId: this.otjData.id }, err);
          this.$ktoast.showError(err, true);
        }).then(() => {
          this.saving = false;
        });
    },

    deleteOtjEntry() {
      this.saving = true;
      this.$logger.info('Privileged user deleting OTJ for learner from dashboard', { otjEntryId: this.otjData.id });
      this.$http.delete(`/api/curriculum/admin/programmes/${this.programmeId}/otj-entries/${this.otjData.id}/${this.learnerId}`)
        .then(() => {
          this.$logger.info('Learner OTJ entry deleted by privileged user', { otjEntryId: this.otjData.id });
          this.$ktoast.success('Entry deleted');
          this.$emit('close');
          this.$emit('update');
        }).catch(err => {
          this.$logger.autowarn('Error deleting learner OTJ entry', undefined, err);
          this.$ktoast.showError(err);
        }).then(() => {
          this.saving = false;
        });
    },

    confirmOtjDeletion() {
      this.$ktoast.confirmToast('You are about to delete this OTJ entry. This cannot be undone.', 'warning', () => {
        this.deleteOtjEntry();
      });
    },

    saveChanges(status) {
      // In compliance mode, we're only ever editing an existing OTJ entry
      if (this.complianceMode) {
        this.updateOtjEntry(status);
        return;
      }

      const previousStatus = this.otjData?.status.toLowerCase();
      if (this.isEditing && previousStatus === 'rejected') {
        // If editing a rejected OTJ, create a new one instead of editing the original
        this.submitRejectedOtj(status);
      } else if (this.isEditing) {
        this.updateOtjEntry(status);
      } else {
        this.createOtjEntry(status);
      }
    },

    clearFields() {
      this.newOtjDateCompleted = new Date();
      this.newOtjStartTime = new Date();
      this.newOtjEndTime = new Date();
      this.newOtjTimeSpent = 0;
      this.newOtjLearningActivityType = undefined;
      this.newOtjLearningDescription = '';
      this.newOtjStatus = undefined;
      this.otjConfirmation = false;

      // Reset checkbox state for all competencies
      if (this.competencies && this.competencies.length > 0) {
        // Create a completely new array to ensure Vue reactivity
        const updatedCompetencies = [];

        // Add each competency with checkbox set to false
        this.competencies.forEach(competency => {
          updatedCompetencies.push({
            ...competency,
            checkbox: false,
          });
        });

        // Replace the entire array to trigger reactivity
        this.competencies = updatedCompetencies;
      }
    },
  },
};

</script>
